import { parse } from 'qs';
import { EDITORS_REGEXP, ROOT_FOLDER_ID, STORAGE_REGEXP } from 'reactApp/constants/magicIdentificators';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import type { IContext, ICtx } from 'server/types/context/IContext';
import type { IRequest } from 'server/types/context/IRequest';

import { getContext } from './context/getContext';
import { isDocumentsDomain } from './isDocumentsDomain';

export interface IRequestParams extends IRequest {
    id: string;
    uri: string;
    uriFull: string;
    userAgentString: string;
    queryString: string;
    cookie: Record<string, string>;
    cookieString: string;
    queryParams: Partial<Record<EQueryParams, string>>;
    httpHeaders: Record<string, string>;
    rbSlotParam: string;
    editor_mode?: string;
}

export enum EQueryParams {
    // Открытие промо после привязки Сферума
    bindSpherum = 'bindSpherum',
    // Превью для RB
    preview = 'preview',
    // Открытие в приложении облака
    webview = 'webview',
    // Открытие в приложении облака для просмтора файлов в webview
    viewerOnly = 'viewerOnly',
    from = 'from',
    'from-page' = 'from-page',
    'from-promo' = 'from-promo',
    // Фичи, которые можно включить через хендлер query
    features = 'features',
    // Хост для подмены статики. Не доступно для прода
    static_host = 'STATIC_HOST',
    action = 'action',
    // Релогин
    'x-email' = 'x-email',

    // Режим приложения
    mode = 'mode',

    // Параметры с бывшего лендинга tariffs
    web_product = 'web_product',
    mt_link_id = 'mt_link_id',

    // Параметр подставляется в урл если открыть файл и у него будет публичная ссылка
    weblink = 'weblink',

    // Ссылка для резервной авторизации
    emergencylink = 'emergencylink',

    // Параметр для поднятия окна оплаты с выбранным тарифом
    tariffId = 'tariffId',

    // Параметр для поднятия окна оплаты с выбранным продуктом
    productId = 'productId',

    // Id сториза, который открывается вместе с action=show-stories
    id = 'id',

    // Параметр для вебвью Майтима
    wv = 'wv',

    // backUrl в attaches
    back_url = 'back_url',

    // Параметры из инвайт-ссылки
    token = 'token',
    owner = 'owner',
    recipient = 'recipient',
    owner_email = 'owner_email',
    folder_name = 'folder_name',

    // Параметры для аттачей
    type = 'type',
    folderId = 'folderId',

    // Параметр для открытия шторки с подписками юзера
    my_subscriptions = 'my_subscriptions',

    // Параметр, в котором передаётся ориджин страницы, открывшей Облако window.open'ом. Валидируется из
    // онлайнконфовой ручки integration-parent-origin-whitelist, используется для отправки postMessage
    parentOrigin = 'parent_origin',
    // Идентификатор клиента, который интегрировал себе Облако. Используется для аналитики
    integrationClient = 'integration_client',
    integrationP = 'integration_p',
    integrationUid = 'integration_uid',
    integrationChannel = 'integration_channel',
    // Просмотрщик в вэбвью https://jira.vk.team/browse/CLOUDWEB-15881
    is_app_viewer = 'is_app_viewer',
    // идентификатор сессии
    aimsid = 'aimsid',
    // Тип авторизации (куки или o2 токены)
    auth = 'auth',
    // Фиксируем, что именно нажали
    requiredSignUpTouchSource = 'required_sign_up_touch_source',

    // Параметр для WebView внутри суперапа, равен android или ios
    fromDeeplink = 'from_deeplink',

    // Параметр для WebView внутри суперапа, используется при postMessage

    version = 'version',

    // Показываем выбор темы
    themeChooser = 'themeChooser',

    // Название мероприятия на лендинге мероприятий
    name = 'name',
    // Включить темную тему в модальном окне отключения рекламы почты
    darkTheme = 'darkTheme',
    // Форсит включение определенноый темы с помощью квери параметра
    theme = 'theme',

    /**
     * "touch public stub seen"
     *
     * Флаг, что мы пришли со страницы тач паблика со стабом и уже посмотрели рекламу
     */
    tpss = 'tpss',

    // Форсит включение определенной темы с помощью квери параметра
    utm_source = 'utm_source',
    // Кастомный заголовок в окне оплаты, задается в onlineconf /features/tariff-buy-custom-title
    title = 'title',
    'need_silent' = 'need_silent',

    // Параметр для workspace superapp (deprecated)
    superapp = 'superapp',

    // Источник запуска приложения
    source = 'source',

    // Диплинк для открытия в почте
    mail_deeplink = 'mail_deeplink',

    // Ссылка для открытия в webview в диплинке
    url = 'url',

    // маркер v2-flow может быть cookie или token
    // https://gitlab.corp.mail.ru/vk-workspace/superapp/-/blob/master/src/services/miniapp-auth/README.md
    wssa_auth_type = 'wssa_auth_type',

    // маркер v2-flow может быть со значением v2
    // https://gitlab.corp.mail.ru/vk-workspace/superapp/-/blob/master/docs/specs/mini-app-auth.spec.md?ref_type=heads
    wssa_auth_flow = 'wssa_auth_flow',
}

const getCookie = (httpCookie: string): Record<string, string> => {
    if (!httpCookie) {
        return {};
    }

    // Иногда бек присылает энкоженый ';' в виде '%3B%20'. Не сделал обработку всей строки через decodeURIcomponent, так как в значениях кук бывают энкоженные данные.
    return httpCookie
        ?.replace(/%3B%20/g, ';')
        ?.split(';')
        .reduce((res, cookie: string) => {
            const parts = cookie.split('=');
            const key = parts.shift()?.trim() || '';

            try {
                res[key] = decodeURIComponent(parts.join('='));
                return res;
            } catch (_) {
                return '';
            }
        }, {});
};

const getQueryParams = (queryString: string): Partial<Record<EQueryParams, string>> => {
    if (!queryString) {
        return {};
    }

    try {
        return parse(queryString, { ignoreQueryPrefix: true });
    } catch (_) {
        return {};
    }
};

const getHttpHeaders = (httpHeaders = ''): Record<string, string> => {
    try {
        return JSON.parse(httpHeaders);
    } catch (_) {
        return {};
    }
};

const parseAttachId = (uri: string) => {
    const uriDecoded = decodeURIComponent(uri);
    if (!uriDecoded.match(/\/attaches\/*$/)) {
        if (uriDecoded.match(/(\/)attaches(\/)(\d+)(\/|;)(\d+);(\d+)/)) {
            return uriDecoded.replace(/(\/)attaches(\/)(\d+)(\/|;)(\d+);(\d+)/, '$3;$5;$6').replace(/^\//, '');
        }

        // eslint-disable-next-line no-useless-escape
        if (uriDecoded.match(/(\/)attaches(\/)([A-Za-z0-9]+)(?:[\/|;|%3B])/)) {
            return uriDecoded.replace(/(\/)attaches(\/)([A-Za-z0-9]+)\/(\/public\/)?([A-Za-z0-9]+)/, '$3;$4$5').replace(/^\//, '');
        }
    }

    return '';
};

const getRequestId = (request: IRequest) => {
    const storage = request.storage;

    if (storage === EStorageType.home) {
        return request.home;
    }

    if (storage === EStorageType.public) {
        return request.weblink;
    }

    if (storage === EStorageType.stock) {
        return request.stock;
    }

    if (storage === EStorageType.attaches) {
        return parseAttachId(request.uri.replace(/\?.+$/g, ''));
    }

    return ROOT_FOLDER_ID;
};

const cleanUri = (uri: IRequest['uri']) => uri.replace(/\?.*$/g, '');

export const getStorageFromUri = (uri: string) => {
    const customStorage = uri.match(STORAGE_REGEXP);

    const storage = customStorage?.[1];

    if (!storage && uri.startsWith('/shared/indexed')) {
        return EStorageType.sharedLinks;
    }

    if (!storage && uri.startsWith('/test-embedded')) {
        return EStorageType.embedded;
    }

    if (storage?.startsWith('doc/')) {
        // Открываем редактор по уникальному id, берем storage из json.request.storage
        return null;
    }

    return storage;
};

const isEditorUri = (uri: string) => uri.match(EDITORS_REGEXP);

const getStorageFromRequest = (request: IRequest) => {
    const uri = cleanUri(request.uri);
    const storage = getStorageFromUri(uri);
    const isSearch = storage === EStorageType.search;
    const isAllDocuments =
        (isDocumentsDomain(request.host) && !isEditorUri(request.uri) && !isSearch) || storage === EStorageType.alldocuments;

    return isAllDocuments ? EStorageType.alldocuments : storage;
};

const getUriFromRequest = (request: IRequest) => {
    return isDocumentsDomain(request.host) ? request.uri.replace('/home', '') : request.uri;
};

export const getRequestParams = (ctx: ICtx, json: IContext): IRequestParams => {
    const uri = getUriFromRequest(json.request);
    const httpCookie = getContext(ctx, 'HTTP_COOKIE') || '';
    const queryString = uri.replace(/^.+\?/g, '');
    const userAgentString = getContext(ctx, 'HTTP_USER_AGENT') || '';
    const httpHeaders = getHttpHeaders(getContext(ctx, 'HTTP_HEADERS'));

    const cookie = getCookie(httpCookie);
    const queryParams = getQueryParams(queryString);
    const id = getRequestId(json.request);
    const preview = queryParams.preview;
    const rbSlotParam = preview ? `preview=${preview}` : '';
    const storage = (getStorageFromRequest(json.request) || json.request.storage) as EStorageType;

    return {
        ...json.request,
        id: id?.replace(/\/+$/g, '') || ROOT_FOLDER_ID,
        storage,
        cookie,
        cookieString: httpCookie,
        queryParams,
        userAgentString,
        uriFull: `https://${json.request.host}${uri}`,
        uri: cleanUri(uri),
        httpHeaders,
        queryString,
        rbSlotParam,
    };
};
